import { usePlatformStore, useChatSheet } from "@platform/store";
import { useFetchTalkJSData, type TalkJSData } from "@core/features/chat";
import { useFetchUserPrograms } from "@core/features/program";

/**
 * Resolve the program that scopes the chat surface for the current page.
 *
 * Reads the meeting-page override (set by `useScopedProgramOverride` on
 * `/video/meeting/:id` and `/video/end/:id`) when present; otherwise
 * falls back to the user's first-enrolled program. Centralized here so
 * the session-init query and any sibling per-program chat state agree
 * on which program drives the surface — and so any future change to
 * the resolution rule lives in one place.
 *
 * Returns `null` until the underlying data has loaded (no user, no
 * programs yet). Consumers should gate downstream queries on a
 * non-null result.
 */
export function useChatProgramId(): number | null {
  const user = usePlatformStore((s) => s.user);
  const programIdOverride = usePlatformStore((s) => s.programIdOverride);
  const { data: programsData } = useFetchUserPrograms({ user });
  return programIdOverride ?? programsData?.programs?.[0]?.id ?? null;
}

/**
 * Get TalkJS data from React Query.
 * This is the source of truth for TalkJS configuration.
 * Respects the isChatDisabled feature flag.
 */
export function useTalkJSData(): TalkJSData | null {
  const user = usePlatformStore((s) => s.user);
  const isChatDisabled = usePlatformStore((s) => s.featureFlags.isChatDisabled);
  const programId = useChatProgramId();
  const { data } = useFetchTalkJSData({
    user,
    programId,
    isEnabled: !isChatDisabled,
  });

  return data ?? null;
}

/**
 * Get chat actions from Zustand store.
 * Only opens chat if TalkJS data is available.
 */
export function useChatActions() {
  const {
    open: openChatStore,
    close: closeChat,
    toggle: toggleChat,
  } = useChatSheet();
  const talkjsData = useTalkJSData();

  const openChat = () => {
    if (talkjsData) {
      openChatStore();
    }
    // Optionally, you could show a toast or feedback if talkjsData is missing
  };

  return {
    openChat,
    closeChat,
    toggleChat,
  };
}

/**
 * Get chat state from Zustand store.
 * @deprecated Use useChatSheet from @platform/store directly
 */
export function useChat() {
  const { isOpen } = useChatSheet();
  return { isOpen };
}

